import React, { useState } from 'react';
import { CreditCardIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PlusIcon, SearchIcon, FilterIcon, RefreshCwIcon } from 'lucide-react';
interface Terminal {
  id: string;
  name: string;
  serialNumber: string;
  model: string;
  brand: string;
  status: 'online' | 'offline' | 'maintenance';
  location: string;
  lastConnection: string;
  transactionVolume: number;
  batteryLevel?: number;
  firmwareVersion: string;
}
export function PhysicalTerminals() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Mock data for physical terminals - updated to PAX terminals with resort-specific names
  const terminals: Terminal[] = [{
    id: 'term-001',
    name: 'Front Office 1',
    serialNumber: 'PAX-A80-12345',
    model: 'PAX A80',
    brand: 'PAX',
    status: 'online',
    location: 'Main Reception',
    lastConnection: '2 minutes ago',
    transactionVolume: 12450,
    batteryLevel: 85,
    firmwareVersion: '4.3.1'
  }, {
    id: 'term-002',
    name: 'Front Office 2',
    serialNumber: 'PAX-A920-98765',
    model: 'PAX A920',
    brand: 'PAX',
    status: 'online',
    location: 'Main Reception',
    lastConnection: '5 minutes ago',
    transactionVolume: 9870,
    batteryLevel: 72,
    firmwareVersion: '2.1.0'
  }, {
    id: 'term-003',
    name: 'Oblu Restaurant',
    serialNumber: 'PAX-A80-34567',
    model: 'PAX A80',
    brand: 'PAX',
    status: 'offline',
    location: 'Main Restaurant',
    lastConnection: '3 days ago',
    transactionVolume: 5430,
    batteryLevel: 0,
    firmwareVersion: '3.0.2'
  }, {
    id: 'term-004',
    name: 'Ocean Bar',
    serialNumber: 'PAX-A920-56789',
    model: 'PAX A920',
    brand: 'PAX',
    status: 'online',
    location: 'Beachfront',
    lastConnection: '17 minutes ago',
    transactionVolume: 3250,
    batteryLevel: 45,
    firmwareVersion: '4.2.1'
  }, {
    id: 'term-005',
    name: 'Spa Reception',
    serialNumber: 'PAX-A800-54321',
    model: 'PAX A800',
    brand: 'PAX',
    status: 'maintenance',
    location: 'Wellness Center',
    lastConnection: '1 day ago',
    transactionVolume: 7650,
    batteryLevel: 100,
    firmwareVersion: '1.9.5'
  }, {
    id: 'term-006',
    name: 'Water Sports Kiosk',
    serialNumber: 'PAX-S900-67890',
    model: 'PAX S900',
    brand: 'PAX',
    status: 'online',
    location: 'Beach Activity Center',
    lastConnection: '1 hour ago',
    transactionVolume: 2180,
    firmwareVersion: '2.5.0'
  }, {
    id: 'term-007',
    name: 'Sunset Lounge',
    serialNumber: 'PAX-A920-24680',
    model: 'PAX A920',
    brand: 'PAX',
    status: 'online',
    location: 'Rooftop Bar',
    lastConnection: '10 minutes ago',
    transactionVolume: 8920,
    firmwareVersion: '3.1.2'
  }, {
    id: 'term-008',
    name: 'Gift Shop',
    serialNumber: 'PAX-A800-13579',
    model: 'PAX A800',
    brand: 'PAX',
    status: 'offline',
    location: 'Lobby Area',
    lastConnection: '2 days ago',
    transactionVolume: 1540,
    batteryLevel: 0,
    firmwareVersion: '2.0.3'
  }];
  // Filter terminals based on search query and status filter
  const filteredTerminals = terminals.filter(terminal => {
    const matchesSearch = terminal.name.toLowerCase().includes(searchQuery.toLowerCase()) || terminal.serialNumber.toLowerCase().includes(searchQuery.toLowerCase()) || terminal.model.toLowerCase().includes(searchQuery.toLowerCase()) || terminal.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || terminal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Helper function for status badge
  const getStatusBadge = (status: 'online' | 'offline' | 'maintenance') => {
    switch (status) {
      case 'online':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1971">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-1972" />
            Online
          </span>;
      case 'offline':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1973">
            <XCircleIcon size={12} className="mr-1" data-id="element-1974" />
            Offline
          </span>;
      case 'maintenance':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1975">
            <AlertTriangleIcon size={12} className="mr-1" data-id="element-1976" />
            Maintenance
          </span>;
    }
  };
  // Helper function for battery indicator
  const getBatteryIndicator = (level?: number) => {
    if (level === undefined) return null;
    let bgColor = 'bg-green-500';
    if (level < 20) bgColor = 'bg-red-500';else if (level < 50) bgColor = 'bg-yellow-500';
    return <div className="flex items-center" data-id="element-1977">
        <div className="w-8 h-3 border border-gray-400 rounded-sm mr-1 relative" data-id="element-1978">
          <div className={`absolute top-0 left-0 bottom-0 ${bgColor} rounded-sm`} style={{
          width: `${level}%`
        }} data-id="element-1979"></div>
          <div className="absolute top-0 right-0 w-0.5 h-3 -mr-0.5 bg-gray-400 rounded-r-sm" data-id="element-1980"></div>
        </div>
        <span className="text-xs text-gray-600" data-id="element-1981">{level}%</span>
      </div>;
  };
  return <div className="space-y-6" data-id="element-1982">
      <div className="flex justify-between items-center" data-id="element-1983">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-1984">
          <CreditCardIcon size={24} className="mr-2 text-blue-600" data-id="element-1985" />
          Physical Terminals
        </h1>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors" data-id="element-1986">
          <PlusIcon size={16} className="mr-2" data-id="element-1987" />
          Add Terminal
        </button>
      </div>
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-1988">
        {/* Filter and search bar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4" data-id="element-1989">
          <div className="relative flex-grow" data-id="element-1990">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1991">
              <SearchIcon size={16} className="text-gray-400" data-id="element-1992" />
            </div>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search terminals by name, serial number, model..." data-id="element-1993" />
          </div>
          <div className="flex space-x-2" data-id="element-1994">
            <div className="relative" data-id="element-1995">
              <div className="flex items-center" data-id="element-1996">
                <FilterIcon size={16} className="text-gray-500 mr-2" data-id="element-1997" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-1998">
                  <option value="all" data-id="element-1999">All Status</option>
                  <option value="online" data-id="element-2000">Online</option>
                  <option value="offline" data-id="element-2001">Offline</option>
                  <option value="maintenance" data-id="element-2002">Maintenance</option>
                </select>
              </div>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2003">
              <RefreshCwIcon size={16} className="mr-2" data-id="element-2004" />
              Refresh
            </button>
          </div>
        </div>
        {/* Terminal grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4" data-id="element-2005">
          {filteredTerminals.map(terminal => <div key={terminal.id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-2006">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-2007">
                <div className="font-medium text-gray-800" data-id="element-2008">{terminal.name}</div>
                {getStatusBadge(terminal.status)}
              </div>
              <div className="p-4 space-y-3" data-id="element-2009">
                <div className="flex items-center" data-id="element-2010">
                  <CreditCardIcon size={36} className="text-blue-600 mr-3" data-id="element-2011" />
                  <div data-id="element-2012">
                    <div className="font-medium" data-id="element-2013">{terminal.model}</div>
                    <div className="text-sm text-gray-600" data-id="element-2014">
                      {terminal.brand}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-2015">
                  <div data-id="element-2016">
                    <div className="text-gray-500" data-id="element-2017">Serial Number</div>
                    <div data-id="element-2018">{terminal.serialNumber}</div>
                  </div>
                  <div data-id="element-2019">
                    <div className="text-gray-500" data-id="element-2020">Location</div>
                    <div data-id="element-2021">{terminal.location}</div>
                  </div>
                  <div data-id="element-2022">
                    <div className="text-gray-500" data-id="element-2023">Last Connection</div>
                    <div data-id="element-2024">{terminal.lastConnection}</div>
                  </div>
                  <div data-id="element-2025">
                    <div className="text-gray-500" data-id="element-2026">Transaction Volume</div>
                    <div data-id="element-2027">${terminal.transactionVolume.toLocaleString()}</div>
                  </div>
                  <div data-id="element-2028">
                    <div className="text-gray-500" data-id="element-2029">Firmware</div>
                    <div data-id="element-2030">{terminal.firmwareVersion}</div>
                  </div>
                  {terminal.batteryLevel !== undefined && <div data-id="element-2031">
                      <div className="text-gray-500" data-id="element-2032">Battery</div>
                      {getBatteryIndicator(terminal.batteryLevel)}
                    </div>}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-2033">
                <div className="flex justify-between" data-id="element-2034">
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-2035">
                    View Details
                  </button>
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-2036">
                    Update Firmware
                  </button>
                </div>
              </div>
            </div>)}
        </div>
        {/* Empty state */}
        {filteredTerminals.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-2037">
            <CreditCardIcon size={48} className="text-gray-400 mb-4" data-id="element-2038" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2039">
              No terminals found
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-2040">
              No terminals match your current search criteria. Try adjusting
              your filters or add a new terminal.
            </p>
          </div>}
      </div>
    </div>;
}